/**
 * Builds the client-side sponsored-ad capture bookmarklet.
 *
 * The operator drags this to their bookmarks bar once, then clicks it on a
 * google.com results page. It scrapes the live ad block IN THEIR OWN browser
 * session (real ads, rarely a CAPTCHA) and opens a NEW TAB back to the app
 * with the scraped ads in the URL fragment.
 *
 * Why window.open(app#sac=...) instead of fetch():
 *  - A navigation isn't subject to google.com's CSP/connect-src, and carries
 *    no cross-origin/auth baggage.
 *  - The new tab IS the feedback — no silent failure, no polling, no waiting.
 *  - The app reads the fragment and renders the ads directly (and persists
 *    them from its own origin).
 *
 * `appUrl` is the app page to reopen (usually the current Sponsored Ads tab
 * URL). Ad data rides in the `#sac=` fragment, which the panel parses on load.
 */
export function buildSponsoredAdBookmarklet(appUrl: string): string {
  // Selectors derived from the live PH 'marketing company' SERP: Google text
  // ads are [data-text-ad] blocks with the title in [role=heading], the
  // advertiser/URL/description as innerText lines (NO <cite>), and sitelinks in
  // the links after the first. Fallback: headings inside #tads/#tvcap/#bottomads.
  const body = `(function(){try{
var P=new URLSearchParams(location.search);
var kw=P.get('q')||'';var gl=P.get('gl')||'';
function T(el){return el?(el.textContent||'').trim():'';}
function host(u){try{return new URL(u,location.href).hostname.replace(/^www\\./,'');}catch(e){return '';}}
var blocks=[].slice.call(document.querySelectorAll('[data-text-ad]'));
if(!blocks.length){
blocks=[].slice.call(document.querySelectorAll('#tads [role=heading],#tvcap [role=heading],#bottomads [role=heading]')).map(function(h){return h.closest('[data-text-ad]')||h.closest('div');}).filter(Boolean);
}
var ads=[];var seen={};
blocks.forEach(function(el){
// Real ad layout (innerText, one item per line):
//   [0] headline  [1..u-1] advertiser  [u] display URL  [u+1] snippet
//   then sitelink (title, description) pairs. Sitelink <a> textContent is
//   "TitleDescription" concatenated, so clean titles come from the lines.
var lines=(el.innerText||'').split('\\n').map(function(s){return s.trim();}).filter(Boolean);
if(!lines.length)return;
var ext=[].slice.call(el.querySelectorAll('a[href]')).filter(function(a){var hh=host(a.href);return hh&&hh.indexOf('google')<0;});
var mainHref=ext.length?ext[0].href:(el.querySelector('a[href]')?el.querySelector('a[href]').href:'');
var dom=host(mainHref);
var title=lines[0];
if(!title&&!mainHref)return;
var key=dom+'|'+title;if(seen[key])return;seen[key]=1;
var u=-1;
for(var i=1;i<lines.length;i++){if(/^https?:\\/\\//.test(lines[i])||lines[i].indexOf('\\u203a')>=0){u=i;break;}}
var advertiser='';var disp='';var snip='';
if(u>0){advertiser=lines.slice(1,u).join(' ');disp=lines[u];snip=lines[u+1]||'';}
else{disp=dom;for(var j=1;j<lines.length;j++){if(lines[j].length>20){snip=lines[j];break;}}}
var tail=u>=0?lines.slice(u+2):[];
var sl=[];
ext.slice(1).forEach(function(a){
var at=(a.textContent||'').replace(/\\s+/g,' ').trim();
var ttl='';
for(var n=0;n<tail.length;n++){if(tail[n].length>=2&&at.indexOf(tail[n])===0){ttl=tail[n];break;}}
if(!ttl&&at)ttl=at.slice(0,40);
if(ttl&&ttl.length<=60&&sl.indexOf(ttl)<0)sl.push(ttl);
});
ads.push({title:title,advertiser:advertiser,href:mainHref,display_url:disp||dom,snippet:snip,sitelinks:sl,position:ads.length+1});
});
var u=${JSON.stringify(appUrl)}+'#sac='+encodeURIComponent(JSON.stringify({keyword:kw,gl:gl,sponsored_results:ads}));
window.open(u,'_blank');
}catch(e){alert('Capture failed: '+e.message);}})();`;
  return 'javascript:' + body.replace(/\n/g, '');
}

/** Encode a Google `uule` location parameter from a canonical location name
 * (e.g. "Utah,United States"). `gl` only targets countries; `uule` targets a
 * specific region/city/DMA, so the SERP is viewed as if from that location.
 * Standard canonical-name algorithm: key + chr(byteLen) + base64(name). */
export function encodeUule(canonicalName: string): string {
  const name = (canonicalName || '').trim();
  if (!name) return '';
  // UTF-8 byte length (uule prefixes the length byte).
  const utf8 = typeof TextEncoder !== 'undefined'
    ? new TextEncoder().encode(name)
    : null;
  const byteLen = utf8 ? utf8.length : unescape(encodeURIComponent(name)).length;
  const b64 = btoa(unescape(encodeURIComponent(name)));
  return 'w+CAIQICI' + String.fromCharCode(byteLen) + b64;
}

/** Build a geo-targeted Google search URL for a query + market. `gl` is the
 * 2-letter country; pass `uule` (from encodeUule) for sub-country precision
 * (state/city). Used to give the operator a one-click link to the exact SERP
 * the bookmarklet should be run on. */
export function buildGoogleSearchUrl(
  query: string,
  gl: string = 'us',
  hl: string = 'en',
  uule: string = '',
): string {
  const params = new URLSearchParams({ q: query.trim() });
  if (gl) params.set('gl', gl.toLowerCase());
  if (hl) params.set('hl', hl);
  if (uule) params.set('uule', uule);
  return `https://www.google.com/search?${params.toString()}`;
}

const HASH_PREFIX = '#sac=';

export interface ParsedHashCapture {
  keyword: string;
  gl: string;
  sponsored_results: Array<Record<string, unknown>>;
}

/** Parse the `#sac=` capture fragment the bookmarklet appends. Returns null
 * when the hash isn't a capture or can't be parsed. */
export function parseSponsoredAdHash(hash: string): ParsedHashCapture | null {
  if (!hash || !hash.startsWith(HASH_PREFIX)) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(hash.slice(HASH_PREFIX.length)));
    if (!parsed || !Array.isArray(parsed.sponsored_results)) return null;
    return {
      keyword: typeof parsed.keyword === 'string' ? parsed.keyword : '',
      gl: typeof parsed.gl === 'string' ? parsed.gl : '',
      sponsored_results: parsed.sponsored_results,
    };
  } catch {
    return null;
  }
}
